import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Checkbox, Dialog, Text } from '@viapeople/components'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { FileUploadModal } from '../../../components/FileUploadModal'
import type { FileUploadRequest } from '../../../components/FileUploadModal/types'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'

const ALLOWED_FILE_TYPES = [
	'application/vnd.ms-excel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'text/csv',
]

const FILE_EXTENSIONS = '.csv, .xls, .xlsx'

interface UserUploadModalProps {
	isOpen: boolean
	onClose: () => void
	onUploadSuccess: () => void
}

export const UserUploadModal: React.FC<UserUploadModalProps> = ({ isOpen, onClose, onUploadSuccess }) => {
	const { organizationId } = useParams<{ organizationId: string }>()
	const queryClient = useQueryClient()
	const toast = useToast()
	const [autoProvisionOktaUsers, setAutoProvisionOktaUsers] = useState(false)
	const [showOktaConfirmDialog, setShowOktaConfirmDialog] = useState(false)
	const [parseHierarchy, setParseHierarchy] = useState(false)

	useEffect(() => {
		if (isOpen) {
			setAutoProvisionOktaUsers(false)
			setParseHierarchy(false)
		}
	}, [isOpen])

	const { mutateAsync: userFileUploadRequest } = useMutation({
		...trpc.admin.userFiles.requestFileUpload.mutationOptions(),
	})

	const { mutateAsync: acknowledgeFileUpload } = useMutation({
		...trpc.admin.userFiles.acknowledgeFileUpload.mutationOptions(),
	})

	const { mutateAsync: processFileUpload } = useMutation({
		...trpc.admin.userFiles.processFile.mutationOptions(),
		onSuccess: (response) => {
			if (!response.isValid) return
			queryClient.invalidateQueries({
				queryKey: trpc.admin.users.search.queryKey({ organizationId: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.userHistory.list.queryKey(),
				exact: false,
				refetchType: 'all',
			})
			if (parseHierarchy) {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.hierarchyFiles.search.queryKey({ organizationId: organizationId as string }),
					exact: false,
					refetchType: 'all',
				})
			}
		},
	})

	const uploadRequest: FileUploadRequest = {
		requestUpload: async (data) => {
			const result = await userFileUploadRequest({
				...data,
				organizationId: organizationId as string,
			})
			return result
		},
		acknowledgeUpload: async (data) => {
			const result = await acknowledgeFileUpload({
				...data,
				organizationId: organizationId as string,
			})
			return result
		},
		processFile: async (data: { fileId: string; columnMapping: Record<string, string> }) => {
			const response = await processFileUpload({
				...data,
				organizationId: organizationId as string,
				firstNameHeader: data.columnMapping.firstName,
				lastNameHeader: data.columnMapping.lastName,
				emailHeader: data.columnMapping.email,
				externalIdHeader: data.columnMapping.id,
				autoProvisionOktaUsers,
				...(parseHierarchy
					? {
							primaryManagerIdHeader: data.columnMapping.primaryManagerId,
							highLevelManagerIdHeader: data.columnMapping.secondaryManagerId || undefined,
							coManagerIdsHeader: data.columnMapping.coManagerIds || undefined,
							hierarchyDeletedIndicatorHeader: data.columnMapping.deletedIndicator || undefined,
						}
					: {}),
			})

			if (!response.isValid) {
				const errors = response.errors
				const firstTen = errors.slice(0, 10)
				const remaining = errors.length > 10 ? errors.length - 10 : 0
				const hasCrossOrgConflict = errors.some((e) => e.includes('already belongs to a user in'))
				const supportHint = hasCrossOrgConflict
					? 'Contact ViaPeople support to migrate users between organizations.'
					: ''
				const errorMsg = [...firstTen, remaining > 0 ? `and ${remaining} more errors` : '', supportHint]
					.filter(Boolean)
					.join('\n')
				throw new Error(errorMsg)
			}

			if (response.oktaProvisioning) {
				const { successCount, failureCount } = response.oktaProvisioning
				if (failureCount > 0) {
					toast.showToast(`Okta provisioning: ${successCount} succeeded, ${failureCount} failed`, 'error')
				} else if (successCount > 0) {
					toast.showToast(`Successfully provisioned ${successCount} users in Okta`, 'info')
				}
			} else if (autoProvisionOktaUsers) {
				toast.showToast('Okta provisioning could not be completed. Please check the configuration.', 'error')
			}
		},
	}

	const hierarchyMappingFields = parseHierarchy
		? [
				{
					key: 'primaryManagerId',
					label: 'Primary Manager ID Column',
					required: true,
					group: 'Hierarchy',
					hint: "The column containing the direct manager's external user ID.",
				},
				{
					key: 'secondaryManagerId',
					label: 'Secondary Manager ID Column',
					required: false,
					group: 'Hierarchy',
					hint: 'A second-level (skip-level) manager. Leave unmapped to skip.',
				},
				{
					key: 'coManagerIds',
					label: 'Co-Manager IDs Column',
					required: false,
					group: 'Hierarchy',
					hint: 'Comma-separated external IDs of peer managers who share responsibility.',
				},
				{
					key: 'deletedIndicator',
					label: 'Deleted Indicator Column',
					required: false,
					group: 'Hierarchy',
					hint: 'Column whose value signals removal from the hierarchy only (e.g. "Terminated", "true", "1"). The user account remains active. Employees not present in the file are left unchanged.',
				},
			]
		: []

	return (
		<FileUploadModal
			isOpen={isOpen}
			onClose={onClose}
			onUploadSuccess={onUploadSuccess}
			fileConfig={{
				allowedTypes: ALLOWED_FILE_TYPES,
				extensions: FILE_EXTENSIONS,
				maxSize: 2 * 1024 * 1024,
			}}
			columnMappingFields={[
				{ key: 'id', label: 'ID Column', required: true, group: 'User Fields' },
				{ key: 'firstName', label: 'First Name Column', required: true, group: 'User Fields' },
				{ key: 'lastName', label: 'Last Name Column', required: true, group: 'User Fields' },
				{ key: 'email', label: 'Email Column', required: true, group: 'User Fields' },
				...hierarchyMappingFields,
			]}
			uploadRequest={uploadRequest}
			title='Upload Users'
			overwriteMessage='This will overwrite your current users data'
			renderBeforeColumnMapping={() => (
				<div className='mb-6 flex flex-col gap-4'>
					<div className='p-4 bg-neutral-50 rounded-lg'>
						<Checkbox
							checked={autoProvisionOktaUsers}
							onChange={(e) => {
								if (e.target.checked) {
									setShowOktaConfirmDialog(true)
								} else {
									setAutoProvisionOktaUsers(false)
								}
							}}
							label='Automatically provision users in Okta (Non-SSO)'
						/>
						<Text variant='xsmall' className='text-neutral-500 mt-2 ml-6'>
							When enabled, new users will be created in Okta and assigned to the Production Clients group. Check this
							box if the client is NOT using SSO.
						</Text>
						<Dialog
							isOpen={showOktaConfirmDialog}
							onClose={() => setShowOktaConfirmDialog(false)}
							onConfirm={() => {
								setAutoProvisionOktaUsers(true)
								setShowOktaConfirmDialog(false)
							}}
							title='Confirm Okta Provisioning'
							message='This will create Okta accounts and send activation emails to new users upon upload. Existing users will not receive duplicate emails. Are you sure you want to enable automatic provisioning?'
							type='confirm'
							confirmLabel='Enable Provisioning'
						/>
					</div>

					<div className='p-4 bg-neutral-50 rounded-lg'>
						<Checkbox
							checked={parseHierarchy}
							onChange={(e) => setParseHierarchy(e.target.checked)}
							label='Parse hierarchy from this file'
						/>
						<Text variant='xsmall' className='text-neutral-500 mt-2 ml-6'>
							When enabled, manager-employee relationships will be extracted from this file and set as the organization
							hierarchy. New cycles will automatically use this hierarchy.
						</Text>
					</div>
				</div>
			)}
		/>
	)
}
